import type { SportType, WorkoutSegment, ZoneDefinition } from '@/types';

/** Garmin Connect — cycling workout editor (matches in-app “Open Garmin Connect” link). */
export const GARMIN_CONNECT_CYCLING_CREATE_URL =
  'https://connect.garmin.com/app/workout/create/cycling';

/** Garmin Connect — running structured workout editor. */
export const GARMIN_CONNECT_RUNNING_CREATE_URL =
  'https://connect.garmin.com/app/workout/create/running';

export function garminConnectCreateUrl(sport: SportType): string {
  return sport === 'run' ? GARMIN_CONNECT_RUNNING_CREATE_URL : GARMIN_CONNECT_CYCLING_CREATE_URL;
}

/** Garmin Connect UI bucket for step type dropdown */
export type GarminPasteCategory = 'warmup' | 'active' | 'cooldown';

export interface GarminPasteStep {
  category: GarminPasteCategory;
  /** Minutes (Garmin “time” duration); >= ~0.05 */
  durationMinutes: number;
  /** % FTP, lower bound (integer preferred for the form) */
  powerLowPct: number;
  /** % FTP, upper bound */
  powerHighPct: number;
  /**
   * Optional explicit Garmin power zone (1-7) — when present, the extension uses the
   * `power.zone` target type instead of computing one from the %FTP midpoint with the
   * extension's hard-coded fallback ladder. Lets the app keep the user's edited zone
   * boundaries authoritative all the way through to the Garmin API request body.
   */
  powerZone?: number;
}

export interface GarminPastePayload {
  version: 1;
  workoutName: string;
  steps: GarminPasteStep[];
  /** Drives sport type + zone targets in the extension (`bike` = power zones, `run` = HR zones). */
  sport: SportType;
}

function segmentCategory(seg: WorkoutSegment): GarminPasteCategory {
  if (seg.type === 'Warmup') return 'warmup';
  if (seg.type === 'Cooldown') return 'cooldown';
  return 'active';
}

function clampPct(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(400, Math.round(n)));
}

/**
 * Resolve which zone (1-based) of `zones` the [`low`, `high`] %FTP range falls into. We pick the
 * zone whose [min, max] interval contains the midpoint — matches the Coggan/Friel convention of
 * naming a step by where its average sits, not by the nearest boundary. Returns `undefined` when
 * no zones are provided or the midpoint sits outside the tabulated range (caller falls back to
 * the extension's static ladder).
 */
function resolveZoneNumber(
  lowPct: number,
  highPct: number,
  zones: readonly ZoneDefinition[] | undefined,
): number | undefined {
  if (!zones || zones.length === 0) return undefined;
  const midFraction = ((lowPct + highPct) / 2) / 100;
  if (!Number.isFinite(midFraction)) return undefined;
  for (let i = 0; i < zones.length; i++) {
    const z = zones[i];
    if (midFraction >= z.min && midFraction <= z.max) return i + 1;
  }
  // Above the last zone — clamp to the highest tier so we never emit zone 0.
  const last = zones[zones.length - 1];
  if (midFraction > last.max) return zones.length;
  return undefined;
}

/**
 * Build payload consumed by the Prompt2Train Chrome extension on Garmin Connect (cycling / %FTP-style targets).
 * Optionally pass `zones` (the active, possibly-edited zone table) so each step carries an explicit
 * `powerZone` derived from the SAME values the user sees in the UI — keeping the editor, the AI
 * prompt, and the Garmin API request in lockstep.
 */
export function buildGarminPastePayload(
  workoutName: string,
  segments: WorkoutSegment[],
  zones?: readonly ZoneDefinition[],
  sport: SportType = 'bike',
): GarminPastePayload | null {
  const list = (segments ?? []).filter((s) => s && (s.duration ?? 0) > 0);
  if (!list.length) return null;

  const steps: GarminPasteStep[] = list.map((seg) => {
    const durationMinutes = Math.max(seg.duration / 60, 1 / 60);
    const low = clampPct(seg.powerLow * 100);
    const high = clampPct(seg.powerHigh * 100);
    const zone = resolveZoneNumber(low, high, zones);
    const step: GarminPasteStep = {
      category: segmentCategory(seg),
      durationMinutes: Math.round(durationMinutes * 1000) / 1000,
      powerLowPct: low,
      powerHighPct: Math.max(low, high),
    };
    if (zone) step.powerZone = zone;
    return step;
  });

  return {
    version: 1,
    workoutName: String(workoutName ?? '').trim() || 'Prompt2Train',
    steps,
    sport,
  };
}
